import React, { FC, RefObject, useEffect, useRef } from 'react';
import { useLocation } from 'react-router';
import { useTranslation } from 'react-i18next';
import { MyAccountContainer } from '@components/organisms/MyAccountContainer';
import { Table } from '@components/atoms/Table';
import { OrderStateBadge } from '@components/molecules/badges/OrderStateBadge';
import { Card } from '@components/atoms/Card';
import { Typography, TypographyTagVariants } from '@components/atoms/Typography';
import { Input, InputTypes, InputVariants } from '@components/atoms/Input';
import { Pagination } from '@components/molecules/Pagination';
import { useMediaQuery } from '@hooks/useMediaQuery';
import { usePermissionChecker, UserPermissions } from '@hooks/useHasPermission';
import { useCustomNavigate } from '@hooks/useCustomNavigate';
import { formatDate, formatPrice } from '@utils/format';
import { OrderState, ResultsType } from '@types';
import { Anchor, AnchorMode } from '@components/atoms/Anchor';
import { useInfiniteScroll } from '@hooks/useInfiniteScroll';
import { useData } from '@hooks/useData';
import { useFilters } from '@hooks/useFilters';
import { Form } from '@components/atoms/Form';
import { Select, SelectType } from '@components/atoms/Select';
import { useGetAllMyAccountStores } from '@services/useGetAllMyAccountStores';
import { useGetMyStoresOrders } from '@services/useGetMyStoresOrders';
import styles from './ManageOrders.module.css';
import { SITE_URLS } from '@constants';

const TEXT_FILTER_ID = 'text-filter';
const STORE_FILTER_ID = 'store-filter';

export const ManageOrders: FC = () => {
    const { navigate } = useCustomNavigate();
    const { can } = usePermissionChecker();
    const { pageToRequest, checkPageToRequest, observerRef: scrollObserverRef } = useInfiniteScroll();
    const { t } = useTranslation();

    const translations = {
        title: t('manage-orders-component.title'),
        searchPlaceholder: t('manage-orders-component.search.placeholder'),
    };

    // if user does not have permission accessMerchantAreas, he can't access this page
    useEffect(() => {
        if (can(UserPermissions.accessMerchantAreas) === false) {
            navigate(`/${SITE_URLS.myProfile}`);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const formRef = useRef<HTMLFormElement>(null);

    return (
        <MyAccountContainer title={translations.title}>
            <>
                <div className="pt-4 pb-2">
                    <Filters formRef={formRef} pageToRequest={pageToRequest} checkPageToRequest={checkPageToRequest} />
                </div>
                <div className="pt-4">
                    <OrdersTable />
                    <div ref={scrollObserverRef} style={{ height: '1px' }} />
                </div>
            </>
        </MyAccountContainer>
    );
};

interface FiltersProps {
    formRef: RefObject<HTMLFormElement>;
    pageToRequest: number;
    checkPageToRequest: () => void;
}

const Filters: FC<FiltersProps> = ({ formRef, pageToRequest, checkPageToRequest }) => {
    const location = useLocation();
    const { request: requestMyStoresOrders } = useGetMyStoresOrders();
    const { myStoresOrders, isLoadingMyStoresOrders, setMyStoresOrders } = useData();
    const { appliedFilters, results, setResults, currentPage, setCurrentPage, setFilterValues } = useFilters();

    useEffect(() => {
        // when unmounting page, reset results
        // to prevent showing old results when returning to this page
        return () => {
            setResults([]);
            setMyStoresOrders(null);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const getTextFilter = appliedFilters.find((item) => item.label === TEXT_FILTER_ID)?.values[0].name;
    let getStoreFilter = appliedFilters.find((item) => item.label === STORE_FILTER_ID)?.values.map((value) => value.name)?.[0];

    useEffect(() => {
        // check if url states a specific store, initially
        const queryParams = new URLSearchParams(location.search);
        const getStoreParam = queryParams.get('store') || '';
        if (getStoreParam) {
            // eslint-disable-next-line react-hooks/exhaustive-deps
            getStoreFilter = getStoreParam;
            setFilterValues(STORE_FILTER_ID, [{ uuid: null, name: getStoreParam }]);
        }
    }, []);

    const fetchOrders = (page: number) => {
        setCurrentPage(page);

        // prevent calls for pages that don't exist
        if (myStoresOrders?.pagination?.total_pages && page > myStoresOrders?.pagination?.total_pages) {
            return;
        }

        requestMyStoresOrders({
            textSearch: getTextFilter,
            stores_uuid: getStoreFilter,
            page: page,
        });
    };

    // request more orders from next page
    useEffect(() => {
        pageToRequest > currentPage && !isLoadingMyStoresOrders && fetchOrders(currentPage + 1);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pageToRequest, isLoadingMyStoresOrders]);

    // request first page whenever a filter changes
    const submitForm = () => fetchOrders(1);

    useEffect(() => {
        !isLoadingMyStoresOrders && formRef?.current?.requestSubmit();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [appliedFilters]);

    // every time the endpoint is called, append the data to results constant
    // if the page being called is the first page, reset results
    useEffect(() => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        setResults((previous: any[]) => {
            return myStoresOrders?.pagination?.current_page === 1
                ? myStoresOrders?.data || []
                : [...previous, ...(myStoresOrders?.data || [])];
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [myStoresOrders]);

    // manually check if next page should be requested
    // in case user's screen is big and isn't possible to scroll to trigger next page request
    useEffect(() => {
        checkPageToRequest();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [results.length]);

    return (
        <MyAccountContainer.Filters>
            <Form formRef={formRef} onSubmit={submitForm}>
                <TextFilter />
                <div className={`d-flex flex-column flex-md-row ${styles['grouped-select-filters']}`}>
                    <StoreFilter />
                </div>
            </Form>
        </MyAccountContainer.Filters>
    );
};

const OrdersTable: FC = () => {
    const pageSize = useMediaQuery();
    const { results: orders } = useFilters();
    const { isLoadingMyStoresOrders } = useData();
    const { t } = useTranslation();

    const translations = {
        idShort: t('orders-management-table-component.id-short'),
        idLong: t('orders-management-table-component.id-long'),
        client: t('orders-management-table-component.client'),
        date: t('orders-management-table-component.date'),
        nProducts: t('orders-management-table-component.n-products'),
        state: t('orders-management-table-component.state'),
        total: t('orders-management-table-component.total'),
        actions: t('orders-management-table-component.actions'),
    };

    return (
        <>
            <Card classes="mb-0">
                <Table
                    borderless={true}
                    headerCols={[
                        {
                            classes: 'text-left',
                            text: ['xs', 'sm'].includes(pageSize) ? translations.idShort : translations.idLong,
                        },
                        { classes: 'text-left', text: translations.client },
                        { classes: 'text-center', hideOnMobile: true, text: translations.date },
                        { classes: 'text-center', hideOnMobile: true, text: translations.nProducts },
                        { classes: 'text-center', text: translations.state },
                        { classes: 'text-center', hideOnMobile: true, text: translations.total },
                        { classes: 'text-end', fit: true, text: translations.actions },
                    ]}
                    contentRows={orders.map((order) => {
                        return [
                            {
                                classes: 'text-left text-muted',
                                content: <Typography fontSize="14">{order.order_code}</Typography>,
                            },
                            { classes: 'text-left', content: <Typography fontSize="14">{order.client}</Typography> },
                            {
                                classes: 'text-center',
                                content: <Typography fontSize="14">{formatDate(order.date)}</Typography>,
                            },
                            {
                                classes: 'text-center',
                                content: <Typography fontSize="14">{`${order.nProducts}`}</Typography>,
                            },
                            {
                                classes: 'text-center',
                                content: <OrderStateBadge state={OrderState[order.status as keyof typeof OrderState]} />,
                            },
                            {
                                classes: 'text-center',
                                content: <Typography fontSize="14">{formatPrice(order.total)}</Typography>,
                            },
                            {
                                classes: 'text-end',
                                content: (
                                    <Table.Options
                                        options={[
                                            <Anchor
                                                key={order.uuid}
                                                href={SITE_URLS.manageOrderDetail
                                                    .replace(':order_uuid', `${order.uuid}`)
                                                    .replace(':store_uuid', `${order.store_uuid}`)}
                                                mode={AnchorMode.none}>
                                                <Typography fontSize="16" tagVariant={TypographyTagVariants.span}>
                                                    <i className="fa-light fa-eye" />
                                                </Typography>
                                            </Anchor>,
                                        ]}
                                    />
                                ),
                            },
                        ];
                    })}
                />
            </Card>
            <Pagination loading={isLoadingMyStoresOrders} type={ResultsType.orders} />
        </>
    );
};

const TextFilter: FC = () => {
    const pageSize = useMediaQuery();
    const { appliedFilters, setFilterValues } = useFilters();
    const { t } = useTranslation();

    const translations = {
        searchPlaceholder: t('manage-products-component.search.placeholder'),
    };

    return (
        <div className="align-content-center" style={{ width: ['xs', 'sm', 'md'].includes(pageSize) ? '100%' : 'unset' }}>
            <Input
                type={InputTypes.text}
                variant={InputVariants.searchInput}
                placeholder={translations.searchPlaceholder}
                value={appliedFilters.find((item) => item.label === TEXT_FILTER_ID)?.values[0].name || ''}
                onChange={(value) => setFilterValues(TEXT_FILTER_ID, [{ uuid: null, name: value }])}
            />
        </div>
    );
};

const StoreFilter: FC = () => {
    const pageSize = useMediaQuery();
    const { appliedFilters, setFilterValues } = useFilters();
    const { request: requestAllMyAccountStores } = useGetAllMyAccountStores();
    const { allMyAccountStores, isLoadingAllMyAccountStores, isLoadingMyAccountProducts } = useData();
    const { t } = useTranslation();

    const translations = {
        title: t('manage-products-component.store.label'),
        default: t('select-default-option'),
    };

    // call services, if we don't have the information yet
    useEffect(() => {
        !allMyAccountStores && !isLoadingAllMyAccountStores && requestAllMyAccountStores();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [allMyAccountStores]);

    const options = [
        { value: '', text: translations.default },
        ...(allMyAccountStores?.data || [])
            // filter by stores that may have products
            .filter((item) => item.can_have_products)
            .map((item) => {
                return { value: item.uuid || '', text: item.name || '' };
            }),
    ];

    const selected = appliedFilters.find((item) => item.label === STORE_FILTER_ID)?.values.map((value) => value.name)?.[0];

    const handleOnChange: (value: string) => void = (value) => setFilterValues(STORE_FILTER_ID, [{ uuid: null, name: value }]);

    return (
        <div className="d-flex align-items-center">
            <Typography classes="text-nowrap mb-0 me-2" fontSize="16">
                {translations.title}
            </Typography>
            <Select
                classes={['xs', 'sm'].includes(pageSize) ? 'flex-grow-1' : ''}
                width={['xs', 'sm'].includes(pageSize) ? '100%' : '146px'}
                type={SelectType.variant2}
                options={options}
                disabled={allMyAccountStores === null || isLoadingAllMyAccountStores || isLoadingMyAccountProducts}
                value={selected}
                onChange={handleOnChange}
            />
        </div>
    );
};
